// Theme catalog. Each theme overrides a small set of CSS variables on :root
// to re-skin Pebble's surfaces, primary color, and ambient gradient stops.
//
// Themes unlock through gameplay (levels + milestone-count badges).
// Aurora is Pro-only and gets an animated borealis effect (see index.css).

export type ThemeId =
  | 'pebble'
  | 'golden-hour'
  | 'forest-walk'
  | 'twilight'
  | 'ocean-calm'
  | 'garden-rose'
  | 'winter-whisper'
  | 'aurora';

export interface ThemeUnlock {
  /** Earned badge IDs that grant this theme */
  badge?: string;
  /** Minimum level (1-10) that grants this theme */
  level?: number;
  /** Pro-only theme */
  pro?: boolean;
}

export interface Theme {
  id: ThemeId;
  name: string;
  tagline: string;
  /** Small swatches for the picker preview (HSL strings) */
  swatches: [string, string, string];
  /** CSS variable overrides applied to :root */
  vars: Record<string, string>;
  unlock: ThemeUnlock | null; // null = always unlocked (default)
}

export const THEMES: Theme[] = [
  {
    id: 'pebble',
    name: 'Soft & Dreamy',
    tagline: 'The original — pastel & gentle.',
    swatches: ['340 70% 65%', '24 86% 96%', '270 60% 88%'],
    vars: {}, // default
    unlock: null,
  },
  {
    id: 'golden-hour',
    name: 'Golden Hour',
    tagline: 'Warm peach and soft gold light.',
    swatches: ['28 90% 62%', '38 95% 88%', '20 85% 80%'],
    vars: {
      '--primary': '24 88% 60%',
      '--primary-foreground': '0 0% 100%',
      '--ring': '24 88% 60%',
      '--accent': '38 90% 85%',
      '--bg-stop-1': '38 95% 94%',
      '--bg-stop-2': '28 90% 94%',
      '--bg-stop-3': '18 85% 95%',
      '--bg-stop-4': '45 90% 95%',
    },
    unlock: { level: 3 },
  },
  {
    id: 'forest-walk',
    name: 'Forest Walk',
    tagline: 'Sage greens and warm cream.',
    swatches: ['142 30% 45%', '60 40% 92%', '120 25% 75%'],
    vars: {
      '--primary': '142 35% 42%',
      '--primary-foreground': '0 0% 100%',
      '--ring': '142 35% 42%',
      '--accent': '120 30% 85%',
      '--bg-stop-1': '60 45% 95%',
      '--bg-stop-2': '100 35% 94%',
      '--bg-stop-3': '140 30% 94%',
      '--bg-stop-4': '80 40% 95%',
    },
    unlock: { level: 5 },
  },
  {
    id: 'twilight',
    name: 'Twilight',
    tagline: 'Deep purple skies, scattered stars.',
    swatches: ['265 55% 55%', '250 45% 85%', '285 50% 75%'],
    vars: {
      '--primary': '265 55% 55%',
      '--primary-foreground': '0 0% 100%',
      '--ring': '265 55% 55%',
      '--accent': '285 50% 88%',
      '--bg-stop-1': '250 50% 94%',
      '--bg-stop-2': '270 45% 93%',
      '--bg-stop-3': '290 40% 94%',
      '--bg-stop-4': '230 50% 95%',
    },
    unlock: { level: 7 },
  },
  {
    id: 'ocean-calm',
    name: 'Ocean Calm',
    tagline: 'Cool blues and sea-foam light.',
    swatches: ['200 75% 50%', '190 60% 90%', '170 55% 80%'],
    vars: {
      '--primary': '200 75% 48%',
      '--primary-foreground': '0 0% 100%',
      '--ring': '200 75% 48%',
      '--accent': '170 60% 85%',
      '--bg-stop-1': '195 70% 95%',
      '--bg-stop-2': '210 65% 95%',
      '--bg-stop-3': '170 60% 95%',
      '--bg-stop-4': '185 70% 96%',
    },
    unlock: { level: 10 },
  },
  {
    id: 'garden-rose',
    name: 'Garden Rose',
    tagline: 'Rich rose petals over lavender.',
    swatches: ['340 70% 55%', '320 55% 88%', '280 50% 80%'],
    vars: {
      '--primary': '340 75% 55%',
      '--primary-foreground': '0 0% 100%',
      '--ring': '340 75% 55%',
      '--accent': '280 50% 85%',
      '--bg-stop-1': '340 65% 95%',
      '--bg-stop-2': '320 55% 94%',
      '--bg-stop-3': '290 50% 95%',
      '--bg-stop-4': '350 60% 96%',
    },
    unlock: { badge: 'rooted' },
  },
  {
    id: 'winter-whisper',
    name: 'Winter Whisper',
    tagline: 'Icy blues with silver light.',
    swatches: ['205 60% 70%', '210 40% 92%', '220 30% 85%'],
    vars: {
      '--primary': '205 65% 55%',
      '--primary-foreground': '0 0% 100%',
      '--ring': '205 65% 55%',
      '--accent': '210 40% 90%',
      '--bg-stop-1': '210 50% 96%',
      '--bg-stop-2': '195 45% 96%',
      '--bg-stop-3': '220 40% 96%',
      '--bg-stop-4': '230 35% 97%',
    },
    unlock: { badge: 'constellation-m' },
  },
  {
    id: 'aurora',
    name: 'Aurora',
    tagline: 'Northern lights for Pro families. ✨',
    swatches: ['285 75% 65%', '170 70% 80%', '210 80% 75%'],
    vars: {
      '--primary': '285 75% 62%',
      '--primary-foreground': '0 0% 100%',
      '--ring': '285 75% 62%',
      '--accent': '170 65% 85%',
      '--bg-stop-1': '285 70% 95%',
      '--bg-stop-2': '210 75% 95%',
      '--bg-stop-3': '170 70% 95%',
      '--bg-stop-4': '320 65% 95%',
    },
    unlock: { pro: true },
  },
];

export const DEFAULT_THEME: ThemeId = 'pebble';

export function getTheme(id: string): Theme {
  return THEMES.find(t => t.id === id) ?? THEMES[0];
}

/** Apply a theme's CSS variable overrides to :root. Removes any prior overrides. */
const APPLIED_KEYS_ATTR = 'data-pebble-theme-keys';
export function applyTheme(themeId: string) {
  const root = document.documentElement;
  const prev = root.getAttribute(APPLIED_KEYS_ATTR);
  if (prev) {
    for (const k of prev.split(',').filter(Boolean)) {
      root.style.removeProperty(k);
    }
  }
  const theme = getTheme(themeId);
  const keys = Object.keys(theme.vars);
  for (const k of keys) {
    root.style.setProperty(k, theme.vars[k]);
  }
  root.setAttribute(APPLIED_KEYS_ATTR, keys.join(','));
  root.setAttribute('data-theme', theme.id);
}

export interface UnlockEvalContext {
  earnedBadges: Set<string>;
  level: number;
  isPro: boolean;
}

export function isThemeUnlocked(theme: Theme, ctx: UnlockEvalContext): boolean {
  if (!theme.unlock) return true;
  if (theme.unlock.pro && !ctx.isPro) return false;
  if (theme.unlock.badge && !ctx.earnedBadges.has(theme.unlock.badge)) return false;
  if (theme.unlock.level && ctx.level < theme.unlock.level) return false;
  return true;
}

export function unlockHint(theme: Theme): string {
  if (!theme.unlock) return 'Always available';
  if (theme.unlock.pro) return 'Pebble Pro exclusive';
  if (theme.unlock.badge) {
    const map: Record<string, string> = {
      'rooted': 'Log 15 milestones',
      'constellation-m': 'Log 30 milestones',
      'in-bloom': 'Log 5 milestones',
      'kindled': 'Reach a 7-day streak',
    };
    return map[theme.unlock.badge] ?? `Earn the ${theme.unlock.badge} badge`;
  }
  if (theme.unlock.level) return `Reach Level ${theme.unlock.level}`;
  return 'Locked';
}
